import { useCallback, useMemo } from 'react';
import { trpc } from '@org/clients/school';
import { unwrapTrpcData } from '../../utils';
import { matchesPermission, Permission } from '@org/auth';


const hasWildcard = (permissions: Permission[]) =>
  permissions.includes('admin:all') || permissions.includes('*');

export const useAuthorization = () => {
  const { data, isLoading, error } = trpc.auth.getPermissions.useQuery();

  const permissions = useMemo<Permission[]>(() => {
    const unwrapped = unwrapTrpcData<Permission[]>(data);
    return Array.isArray(unwrapped) ? unwrapped : [];
  }, [data]);

  const effectivePermissions = useMemo(() => {
    if (hasWildcard(permissions)) {
      return new Set<Permission>(['*', 'admin:all', ...permissions]);
    }

    return new Set<Permission>(permissions);
  }, [permissions]);

  const isAuthorized = effectivePermissions.size > 0;

  const hasPermission = useCallback(
    (permission: Permission) => {
      if (!permission) {
        return false;
      }

      // Check if any of the user's permissions match the required permission
      for (const granted of effectivePermissions) {
        if (matchesPermission(granted, permission)) {
          return true;
        }
      }

      return false;
    },
    [effectivePermissions],
  );

  const hasAnyPermission = useCallback(
    (required: Permission[]) => {
      if (!required?.length) {
        return true;
      }

      return required.some((permission) => hasPermission(permission));
    },
    [hasPermission],
  );

  const hasEveryPermission = useCallback(
    (required: Permission[]) => {
      if (!required?.length) {
        return true;
      }

      return required.every((permission) => hasPermission(permission));
    },
    [hasPermission],
  );

  return {
    permissions,
    isLoading,
    error,
    isAuthorized,
    hasPermission,
    hasAnyPermission,
    hasEveryPermission,
  };
};


